import {
  addBooking,
  dropMail,
  DropZone,
  formatMoney,
  getMail,
  getMails,
  getPlan,
  NotFound,
  settleMail,
  type BookingInput,
  type MailItem,
  type MailMark,
  type MailOpened,
  type Plan,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useLive } from '../../app/live';
import { BookingDialog } from '../../components/booking/BookingDialog';
import { dayRange } from '../../lib/countryFacts';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; address: string | null; mails: MailItem[] };

/** What may be dropped: a mail, a Wallet pass, a calendar file, a PDF or a picture. */
const ACCEPT =
  '.eml,message/rfc822,.pkpass,application/vnd.apple.pkpass,.ics,text/calendar,' +
  'application/pdf,image/jpeg,image/png,image/webp';

/** A sender by their name: `Booking.com <noreply@booking.com>` is Booking.com. */
export function senderName(sender: string): string {
  const named = /^(.*?)\s*<[^>]*>$/.exec(sender)?.[1]?.replace(/^"|"$/g, '');
  return named === undefined || named === '' ? sender.replace(/[<>]/g, '') : named;
}

/** A day said long: `8 March 2027`. */
function dayLong(day: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

/** `2027-03-08` and 6 nights on: `2027-03-14`. */
function nightsOn(day: string, nights: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + nights * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * Bookings from mail (the `MailImport` mockup): the trip's address to forward confirmations to,
 * a mail or file dropped or its text pasted, the mails received, and one read — what was found,
 * the line of the mail each part came from — to be added, changed first, or left out.
 */
export function MailImport() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id = '' } = useParams();
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [picked, setPicked] = useState<string | null>(null);
  // The mail last read: shown while it is the one picked, so an answer late for another is not.
  const [loaded, setLoaded] = useState<MailOpened | null>(null);
  const [asked, setAsked] = useState(0);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([getPlan(id), getMails(id)]).then(
      ([plan, { address, mails }]) => {
        if (!live) return;
        setState({ kind: 'ready', plan, address, mails });
        setPicked((was) => was ?? mails[0]?.id ?? null);
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, changed, asked]);

  useEffect(() => {
    if (picked === null) return;
    getMail(id, picked).then(setLoaded, () => undefined);
  }, [id, picked, asked]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { plan, address, mails } = state;
  const opened = loaded?.id === picked ? loaded : null;

  // A mail or its words read and kept: picked, the list read again.
  const take = (sent: Promise<MailItem>) => {
    setBusy(true);
    setFailed(false);
    sent.then(
      (mail) => {
        setBusy(false);
        setPasted('');
        setPicked(mail.id);
        setAsked((n) => n + 1);
      },
      () => {
        setBusy(false);
        setFailed(true);
      },
    );
  };
  // What became of the mail's booking said, and both read again.
  const settle = (
    mail: MailOpened,
    status: 'added' | 'left' | 'review',
    booking: string | null = null,
  ) =>
    settleMail(id, mail.id, status, booking).then(
      () => {
        setAsked((n) => n + 1);
      },
      () => {
        setFailed(true);
      },
    );
  // The booking added, then said to be the mail's: the one the plan did not have before.
  const added = (mail: MailOpened, before: Plan, after: Plan) => {
    const had = new Set(before.stops.flatMap((stop) => stop.bookings.map((one) => one.id)));
    const made = after.stops.flatMap((stop) => stop.bookings).find((one) => !had.has(one.id));
    return settle(mail, 'added', made?.id ?? null);
  };

  const received = (at: string) => {
    const when = new Date(at);
    const day = (date: Date) => date.toISOString().slice(0, 10);
    const now = new Date();
    if (day(when) === day(now)) {
      return t('mail.today', {
        time: new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
          hour: '2-digit',
          minute: '2-digit',
        }).format(when),
      });
    }
    if (day(when) === day(new Date(now.getTime() - 86_400_000))) return t('mail.yesterday');
    return new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
      day: 'numeric',
      month: 'short',
    }).format(when);
  };
  const fromOf = (mail: MailItem) =>
    mail.sender === ''
      ? mail.fileName === ''
        ? t('mail.pasted')
        : mail.fileName
      : senderName(mail.sender);

  const reading = opened?.reading ?? null;
  const draft = reading?.draft ?? null;
  const stop = plan.stops.find((one) => one.id === draft?.stop) ?? null;
  const marks = reading?.marks ?? [];
  const markOf = (part: MailMark['part']) => marks.find((mark) => mark.part === part) ?? null;
  const lines = opened?.text?.split('\n').map((line) => line.trim()) ?? [];
  const lineOf = (mark: MailMark) => lines.findIndex((line) => line === mark.line);
  // The mail scrolled to the line a part was read from, when it has that line.
  const show = (mark: MailMark) => {
    document
      .getElementById(`mail-line-${String(lineOf(mark))}`)
      ?.scrollIntoView({ block: 'nearest' });
  };
  // A part of the booking, pressed to see the line it was read from when there is one.
  const field = (part: MailMark['part'] | null, label: string, value: string | null) => {
    if (value === null || value === '') return null;
    const mark = part === null ? null : markOf(part);
    return (
      <>
        <dt>{label}</dt>
        <dd>
          {mark ? (
            <button
              type="button"
              className="mail-source-link"
              onClick={() => {
                show(mark);
              }}
            >
              <mark>{value}</mark>
            </button>
          ) : (
            value
          )}
        </dd>
      </>
    );
  };
  const inputOf = (): BookingInput | null =>
    draft && draft.stop !== null && draft.name !== '' && draft.price !== null
      ? {
          stop: draft.stop,
          kind: draft.kind,
          name: draft.name,
          day: draft.day,
          nights: draft.nights,
          price: draft.price,
          currency: draft.currency,
          pay: draft.pay,
          reference: draft.reference,
          link: draft.link,
          checkIn: draft.checkIn ?? null,
          checkOut: draft.checkOut ?? null,
          flight: draft.flight ?? null,
        }
      : null;
  const ready = inputOf();
  // What the dialog starts from: what was read, when a booking was found.
  const seed = reading?.found ? reading.draft : undefined;
  const fits =
    stop !== null &&
    draft?.day !== null &&
    draft !== null &&
    draft.day >= stop.start &&
    nightsOn(draft.day, draft.nights) <= stop.end;

  return (
    <div className="page mail-page">
      <header className="page-head">
        <h1>{t('mail.title')}</h1>
        <p className="lead">{t('mail.lead')}</p>
      </header>
      <div className="mail-layout">
        <div className="mail-side">
          <section className="panel" aria-labelledby="mail-forward">
            <h2 id="mail-forward" className="panel-title">
              {t('mail.forward')}
            </h2>
            {address === null ? (
              <p className="quiet">{t('mail.noAddress')}</p>
            ) : (
              <>
                <div className="mail-address">
                  <span className="mono">{address}</span>
                  <button
                    type="button"
                    className="button button-small"
                    onClick={() => {
                      void navigator.clipboard.writeText(address).then(() => {
                        setCopied(true);
                      });
                    }}
                  >
                    {t(copied ? 'mail.copied' : 'mail.copy')}
                  </button>
                </div>
                <p className="quiet mail-small">{t('mail.onlyYours')}</p>
              </>
            )}
            <div className="mail-or">
              <span className="caps">{t('mail.or')}</span>
              <DropZone
                label={t('mail.drop')}
                choose={t('mail.choose')}
                hint={t('mail.dropHint')}
                accept={ACCEPT}
                onFile={(file) => {
                  take(dropMail(id, file, file.name));
                }}
              />
              <form
                className="mail-paste"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (pasted.trim() !== '')
                    take(dropMail(id, new Blob([pasted], { type: 'text/plain' })));
                }}
              >
                <label htmlFor="mail-text">{t('mail.paste')}</label>
                <textarea
                  id="mail-text"
                  rows={3}
                  value={pasted}
                  placeholder={t('mail.pasteHint')}
                  onChange={(event) => {
                    setPasted(event.target.value);
                  }}
                />
                <button type="submit" className="button button-small" disabled={busy}>
                  {t(busy ? 'mail.reading' : 'mail.read')}
                </button>
              </form>
              {failed && (
                <p className="form-failed" role="alert">
                  {t('mail.failed')}
                </p>
              )}
            </div>
          </section>
          <section className="panel mail-inbox" aria-labelledby="mail-received">
            <h2 id="mail-received" className="panel-title">
              {t('mail.received', { count: mails.length })}
            </h2>
            {mails.length === 0 && <p className="quiet">{t('mail.none')}</p>}
            <ul>
              {mails.map((mail) => (
                <li key={mail.id}>
                  <button
                    type="button"
                    className="mail-item"
                    aria-current={mail.id === picked ? 'true' : undefined}
                    onClick={() => {
                      setPicked(mail.id);
                    }}
                  >
                    <b>{mail.subject}</b>
                    <span className={`tag mail-tag-${mail.status}`}>
                      {t(`mail.status.${mail.status}`)}
                    </span>
                    <small>
                      {fromOf(mail)} · {received(mail.receivedAt)} ·{' '}
                      {mail.status === 'none' ? t('mail.byHand') : mail.summary.name}
                    </small>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>
        <section className="panel mail-review" aria-labelledby="mail-read">
          {opened === null ? (
            <>
              <h2 id="mail-read" className="panel-title">
                {t('mail.title')}
              </h2>
              <p className="quiet">{t('mail.pick')}</p>
            </>
          ) : (
            <>
              <div className="mail-head">
                <h2 id="mail-read" className="panel-title">
                  {draft && draft.name !== '' ? draft.name : opened.subject} · {fromOf(opened)}
                </h2>
                {reading && reading.found && (
                  <span className="tag mail-tag-added">
                    {t(`mail.readFrom.${reading.read}`)}
                  </span>
                )}
              </div>
              {!opened.kept && <p className="quiet">{t('mail.gone')}</p>}
              {reading && !reading.found && <p className="quiet">{t('mail.notFound')}</p>}
              {draft && reading?.found && (
                <div className="mail-read">
                  <dl className="mail-fields">
                    {field(null, t('mail.kind'), t(`mail.kinds.${draft.kind}`))}
                    {field(
                      null,
                      t('mail.stop'),
                      stop
                        ? `${stop.city} · ${dayRange(stop.start, stop.end, lang)}`
                        : t('mail.noStop'),
                    )}
                    {field(
                      'checkIn',
                      t(draft.kind === 'stay' ? 'mail.checkIn' : 'mail.departs'),
                      draft.day === null
                        ? null
                        : [
                            dayLong(draft.day, lang),
                            draft.checkIn ?? '',
                            stop && draft.checkIn
                              ? t('mail.localTime', { city: stop.city })
                              : '',
                          ]
                            .filter((part) => part !== '')
                            .join(' '),
                    )}
                    {draft.kind === 'stay' &&
                      draft.day !== null &&
                      field(
                        'checkOut',
                        t('mail.checkOut'),
                        [dayLong(nightsOn(draft.day, draft.nights), lang), draft.checkOut ?? '']
                          .filter((part) => part !== '')
                          .join(' '),
                      )}
                    {draft.kind === 'stay' &&
                      field(
                        null,
                        t('mail.nights'),
                        stop
                          ? `${String(draft.nights)} · ${t(fits ? 'mail.fits' : 'mail.longer')}`
                          : String(draft.nights),
                      )}
                    {field(
                      'price',
                      t('mail.price'),
                      draft.price === null
                        ? null
                        : formatMoney(
                            {
                              minor: draft.price,
                              currency: draft.currency ?? plan.trip.currency,
                            },
                            lang,
                          ),
                    )}
                    {field(
                      'pay',
                      t('mail.pay'),
                      t(draft.pay === 'later' ? 'mail.later' : 'mail.paid'),
                    )}
                    {field('reference', t('mail.reference'), draft.reference)}
                    {field('address', t('mail.address'), reading.address)}
                    {field('flight', t('mail.flight'), draft.flight ?? null)}
                    {field(null, t('mail.seat'), reading.seat)}
                  </dl>
                  {opened.text !== null && (
                    <div className="mail-source" aria-label={t('mail.original')} tabIndex={0}>
                      <p className="mail-source-head">
                        {t('mail.from', {
                          sender: opened.sender === '' ? t('mail.pasted') : opened.sender,
                        })}
                        {opened.forwardedBy !== null &&
                          opened.forwardedBy !== opened.sender && (
                            <>
                              <br />
                              {t('mail.forwardedBy', {
                                sender: opened.forwardedBy,
                                when: received(opened.receivedAt),
                              })}
                            </>
                          )}
                      </p>
                      {lines.map((line, at) => {
                        const marked = marks.some((mark) => mark.line === line);
                        return (
                          <span
                            key={`${String(at)} ${line}`}
                            id={`mail-line-${String(at)}`}
                            className="mail-line"
                          >
                            {marked ? <mark>{line}</mark> : line}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="mail-actions">
                {opened.status === 'review' && (
                  <>
                    <button
                      type="button"
                      className="button button-quiet"
                      onClick={() => {
                        void settle(opened, 'left');
                      }}
                    >
                      {t('mail.leave')}
                    </button>
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        setEditing(true);
                      }}
                    >
                      {t('mail.change')}
                    </button>
                    {ready && (
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={() => {
                          addBooking(id, ready).then(
                            (after) => added(opened, plan, after),
                            () => {
                              setFailed(true);
                            },
                          );
                        }}
                      >
                        {t('mail.add')}
                      </button>
                    )}
                  </>
                )}
                {opened.status === 'none' && (
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      setEditing(true);
                    }}
                  >
                    {t('mail.addByHand')}
                  </button>
                )}
                {opened.status === 'left' && (
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      void settle(opened, 'review');
                    }}
                  >
                    {t('mail.again')}
                  </button>
                )}
                {opened.status === 'added' && (
                  <Link className="button" to={`/trips/${id}/bookings`}>
                    {t('mail.seeBookings')}
                  </Link>
                )}
              </div>
              <ol className="quiet mail-notes">
                {(['note1', 'note2', 'note3'] as const).map((note) => (
                  <li key={note}>{t(`mail.${note}`)}</li>
                ))}
              </ol>
            </>
          )}
        </section>
      </div>
      {editing && opened && (
        <BookingDialog
          plan={plan}
          draft={seed}
          save={(input) => addBooking(id, input)}
          onClose={() => {
            setEditing(false);
          }}
          onSaved={(after) => {
            setEditing(false);
            void added(opened, plan, after);
          }}
        />
      )}
    </div>
  );
}
